import React, {useState, useMemo} from "react"
import moment from "moment"
import {useFinanceDataContext} from "../contexts/FinanceDataContext"
import "./ForecastBanner.css"

const ForecastBanner = ({findMinimumProjectedBalance, onForecastDatePress}) => {
    const [isModalVisible, setIsModalVisible] = useState(false)
    const {
        cashFlowItems,
        balances,
        weatherWarningSettings,
        updateWeatherWarningSettings,
        selectedScale,
        balanceScalePresets,
        calculateThresholds,
    } = useFinanceDataContext()

    const defaultSettings = {
        stormWarning: {
            emoji: "⛈️",
            title: "Storm Warning",
            color: "#DC2626",
            recommendation:
                "Your balance is getting low. Consider adding income or reducing expenses",
        },
        rainWarning: {
            emoji: "🌧️",
            title: "Rain Expected",
            color: "#EA580C",
            recommendation: "Keep an eye on your spending in the coming days",
        },
        partlyCloudy: {
            emoji: "⛅",
            title: "Partly Cloudy",
            color: "#0EA5E9",
            recommendation: null,
        },
        clearSkies: {
            emoji: "☀️",
            title: "Clear Skies",
            color: "#22C55E",
            recommendation: null,
        },
    }

    const [editableSettings, setEditableSettings] = useState(
        weatherWarningSettings || defaultSettings
    )

    // Current thresholds based on selected scale
    const currentThresholds = useMemo(() => {
        return calculateThresholds
            ? calculateThresholds(selectedScale)
            : {
                  stormWarning: 500,
                  rainWarning: 1000,
                  partlyCloudy: 2000,
              }
    }, [selectedScale, calculateThresholds])

    const onboardingStatus = useMemo(() => {
        const hasBalance = Object.keys(balances || {}).length > 0
        const incomeCount = (cashFlowItems || []).filter(
            (item) => item.type === "income"
        ).length
        const expenseCount = (cashFlowItems || []).filter(
            (item) => item.type === "expense"
        ).length

        const requirements = {
            hasBalance,
            hasIncome: incomeCount >= 1,
            hasExpenses: expenseCount >= 2,
            itemCounts: {
                balance: hasBalance ? 1 : 0,
                income: incomeCount,
                expenses: expenseCount,
            },
            progress: {
                balance: hasBalance ? 1 : 0,
                income: Math.min(1, incomeCount),
                expenses: Math.min(1, expenseCount / 2),
            },
        }

        const totalProgress =
            (requirements.progress.balance +
                requirements.progress.income +
                requirements.progress.expenses) /
            3

        const meetsMinimumRequirements =
            hasBalance && incomeCount >= 1 && expenseCount >= 2

        return {
            ...requirements,
            hasMinimumItems: meetsMinimumRequirements,
            progress: totalProgress,
        }
    }, [cashFlowItems, balances])

    const getWeatherWarning = (minBalance) => {
        const settings = weatherWarningSettings || defaultSettings
        const balance = minBalance.balance / 100

        if (balance < currentThresholds.stormWarning) {
            return settings.stormWarning
        } else if (balance < currentThresholds.rainWarning) {
            return settings.rainWarning
        } else if (balance < currentThresholds.partlyCloudy) {
            return settings.partlyCloudy
        } else {
            return settings.clearSkies
        }
    }

    const getProgressMessage = (status) => {
        if (
            status.itemCounts.balance === 0 &&
            status.itemCounts.income === 0 &&
            status.itemCounts.expenses === 0
        ) {
            return "Let's get started! Add your first income or expense to see your daily balance forecast"
        }

        const missingItems = []

        if (!status.hasBalance) {
            missingItems.push("current account balance")
        }
        if (!status.hasIncome) {
            missingItems.push("income source")
        }
        if (!status.hasExpenses) {
            const remaining = 2 - status.itemCounts.expenses
            missingItems.push(
                `${remaining} more expense${remaining > 1 ? "s" : ""}`
            )
        }

        if (missingItems.length === 1) {
            return `Almost there! Add your ${missingItems[0]} to complete your financial forecast`
        }

        const lastItem = missingItems.pop()
        return `Getting closer! Add your ${missingItems.join(
            ", "
        )} and ${lastItem} to see your full financial forecast`
    }

    const handleSaveSettings = () => {
        updateWeatherWarningSettings &&
            updateWeatherWarningSettings(editableSettings)
        setIsModalVisible(false)
    }

    const handleScaleChange = (scale) => {
        updateWeatherWarningSettings &&
            updateWeatherWarningSettings(editableSettings, scale)
    }

    if (!onboardingStatus.hasMinimumItems) {
        return (
            <div className='forecast-banner forecast-banner-onboarding'>
                <div className='forecast-banner-content'>
                    <div className='weather-container'>
                        <div className='weather-emoji'>✨</div>
                        <div className='weather-info'>
                            <h3 className='weather-title onboarding-title'>
                                Your Balance Forecast
                            </h3>
                            <p className='weather-text onboarding-text'>
                                {getProgressMessage(onboardingStatus)}
                            </p>
                            
                            <div className='progress-container'>
                                <div className='progress-bar-container'>
                                    <div
                                        className='progress-bar'
                                        style={{
                                            width: `${
                                                onboardingStatus.progress * 100
                                            }%`,
                                        }}
                                    />
                                </div>
                                <div className='counter-text'>
                                    {onboardingStatus.hasBalance ? "✓" : "○"} Balance ·
                                    {onboardingStatus.hasIncome ? "✓" : "○"} Income ·
                                    {onboardingStatus.hasExpenses
                                        ? "✓"
                                        : `${onboardingStatus.itemCounts.expenses}/2`}{" "}
                                    Expenses
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const minBalance =
        findMinimumProjectedBalance && findMinimumProjectedBalance()
    if (!minBalance) return null

    const weather = getWeatherWarning(minBalance)

    return (
        <>
            <div
                className='forecast-banner'
                onClick={() =>
                    onForecastDatePress && onForecastDatePress(minBalance.date)
                }
                style={{backgroundColor: `${weather.color}20`}}
            >
                <div className='forecast-banner-content'>
                    <div className='weather-container'>
                        <div className='weather-content-left'>
                            <div className='weather-emoji'>{weather.emoji}</div>
                            <div className='weather-info'>
                                <h3
                                    className='weather-title'
                                    style={{color: weather.color}}
                                >
                                    {weather.title}
                                </h3>
                                <p
                                    className='weather-text'
                                    style={{color: weather.color}}
                                >
                                    Your balance forecast is $
                                    {parseInt(minBalance.balance / 100)} on{" "}
                                    {moment(minBalance.date).format("MMM D")}
                                </p>
                                {weather.recommendation && (
                                    <p
                                        className='weather-recommendation'
                                        style={{color: weather.color}}
                                    >
                                        {weather.recommendation}
                                    </p>
                                )}
                            </div>
                            
                        </div>
                    </div>

                    <button
                        className='adjust-button'
                        onClick={(e) => {
                            e.stopPropagation()
                            setIsModalVisible(true)
                        }}
                    >
                        Edit
                    </button>
                </div>
            </div>

            {isModalVisible && (
                <div
                    className='modal-overlay'
                    onClick={() => setIsModalVisible(false)}
                >
                    <div
                        className='modal-container'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className='modal-content'>
                            <h3 className='modal-title'>
                                Customize Weather Warnings
                            </h3>

                            <div className='modal-body'>
                                <div className='scale-section'>
                                    <h4 className='section-title'>
                                        Balance Comfort Level
                                    </h4>
                                    {balanceScalePresets &&
                                        Object.entries(balanceScalePresets).map(
                                            ([key, preset]) => (
                                                <div
                                                    key={key}
                                                    className={`scale-option ${
                                                        selectedScale === key
                                                            ? "selected-scale"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        handleScaleChange(key)
                                                    }
                                                >
                                                    <div>
                                                        <h5 className='scale-title'>
                                                            {preset.name}
                                                        </h5>
                                                        <p className='scale-description'>
                                                            {preset.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                </div>

                                <h4 className='section-title'>
                                    Customize Appearances
                                </h4>
                                {Object.entries(editableSettings).map(
                                    ([key, setting]) => (
                                        <div
                                            key={key}
                                            className='setting-section'
                                        >
                                            <h5 className='setting-title'>
                                                {setting.title}
                                            </h5>

                                            <div className='setting-row'>
                                                <label className='setting-label'>
                                                    Emoji:
                                                </label>
                                                <input
                                                    className='emoji-input'
                                                    value={setting.emoji}
                                                    onChange={(e) =>
                                                        setEditableSettings(
                                                            (prev) => ({
                                                                ...prev,
                                                                [key]: {
                                                                    ...prev[
                                                                        key
                                                                    ],
                                                                    emoji: e
                                                                        .target
                                                                        .value,
                                                                },
                                                            })
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div className='setting-row'>
                                                <label className='setting-label'>
                                                    Title:
                                                </label>
                                                <input
                                                    className='text-input'
                                                    value={setting.title}
                                                    onChange={(e) =>
                                                        setEditableSettings(
                                                            (prev) => ({
                                                                ...prev,
                                                                [key]: {
                                                                    ...prev[
                                                                        key
                                                                    ],
                                                                    title: e
                                                                        .target
                                                                        .value,
                                                                },
                                                            })
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>

                            <div className='modal-buttons'>
                                <button
                                    className='modal-button cancel-button'
                                    onClick={() => setIsModalVisible(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className='modal-button save-button'
                                    onClick={handleSaveSettings}
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ForecastBanner